// ============================================================
// carrusel.js — BANDA ROTATIVA A ANCHURA COMPLETA.
//
// QUÉ RESUELVE: el builder no tenía forma de hacer el patrón más común de una home
// comercial — imagen a sangre que cambia cada X segundos, con su propio titular y su
// propio botón por diapositiva. Los tres bloques cercanos se quedaban cortos por el
// MISMO motivo: ninguno tiene un array de slides con titular + CTA propios (`hero` y
// `banner` tienen props planas; `gallery` tiene slider pero sus items son
// {image, caption, tag}).
//
// MODELO DE DATOS (todo editable por el cliente, NADA hardcodeado aquí):
//   slides: [{ image, alt, title, cta, cta_url }]   ← array; el titular y el botón son
//           data-field/contenteditable, la imagen la pone el image-picker (data-array="slides")
//   altura: 'alto' | 'medio' | 'compacto'           ← 100vh / 60vh / 40vh (CSS, data-altura)
//   intervalo: segundos entre slides (0 = sin autoplay)
//   flechas / puntos / pausa_hover: booleanos (defaults en CARRUSEL_FLAGS)
//
// PUEDE OCUPAR EL PUESTO DEL HERO (ver TIPOS_HERO en core/render.js y en
// core_services/block_registry.py): es full-bleed, se lleva el <h1> de la página cuando
// es el primer titular, y reserva el hueco del header fijo en su propio CSS.
//
// INTERACCIÓN: la lleva Splide (animations/splide-init.js) leyendo los data-splide-*
// que emite este render. CERO `on*` inline (el saneo XSS del servidor los borra al
// publicar — gotcha nº13).
//
// EN EL EDITOR (ronda 2) el bloque se ve COMO EN EL SITIO: una sola diapositiva, a su
// altura real y con el mismo layout, más una fila de PESTAÑAS `.editor-only` para elegir
// cuál se edita, añadirla, moverla o borrarla. Splide sigue destruido a propósito
// (splide-init solo monta fuera de editorMode) y las demás diapositivas se ocultan por
// CSS — ver la nota grande junto a `enEdicion`: NO se filtra el array.
// ============================================================
import { safe } from '../utils/contrast.js';
import { atributoDeBoton } from '../utils/boton-estilo.js';
import { ctaAnchorAttrs } from '../utils/links.js';
import { conSecundario } from '../utils/boton-secundario.js';
import { altAttr } from '../utils/alt-text.js';
import { store } from '../core/store.js';
import { atributosEncuadre } from '../utils/encuadre.js';

// ── SSOT de los ajustes del bloque (los consume también editor/tools.js) ──
/** Alturas ofrecidas al cliente. El id viaja en `data-altura` y el CSS lo traduce a vh. */
export const CARRUSEL_ALTURAS = [
    { id: 'alto', label: 'Alto' },
    { id: 'medio', label: 'Medio' },
    { id: 'compacto', label: 'Compacto' },
];
const _ALTURA_IDS = CARRUSEL_ALTURAS.map(a => a.id);

/** Altura válida (default: 'alto', el uso natural de una banda que abre la página). */
export const clampAltura = (v) => {
    const s = String(v == null ? '' : v).trim().toLowerCase();
    return _ALTURA_IDS.indexOf(s) >= 0 ? s : 'alto';
};

/** Segundos entre diapositivas. 0 = SIN autoplay (decisión legítima, no un error). */
export const INTERVALO_DEFAULT = 5;
export const INTERVALO_MAX = 30;
export const clampIntervalo = (v) => {
    if (v === 0 || v === '0') return 0;
    const n = Math.round(Number(v));
    if (Number.isNaN(n)) return INTERVALO_DEFAULT;
    return Math.max(0, Math.min(INTERVALO_MAX, n));
};

// ── Los TRES interruptores del bloque, con su default ────────────────────────────────
// SSOT: la pintan los botones de la barra de ajustes (aquí) y la lee el toggle del
// dispatch (editor/tools.js) para partir del valor EFECTIVO y no del prop crudo.
//
// `pausa_hover` nace en FALSE a propósito, y no solo en la plantilla de inserción: a
// altura 'alto' la banda ocupa la pantalla entera y no hay dónde dejar el ratón sin
// pararla, así que "pausa al pasar por encima" deja el carrusel congelado casi siempre.
// El default vive AQUÍ (y no solo en editor/tools.js) porque la IA SÍ puede generar el
// bloque pero su schema solo lleva `slides` — los ajustes de presentación no están en
// `llm_schemas.py::_carrusel_props` —, así que un carrusel generado llega sin el prop y
// con el default antiguo (ausente ⇒ activado) se habría quedado con la pausa puesta.
// Los carruseles YA guardados llevan `pausa_hover: true` explícito: siguen pausando
// hasta que el cliente lo apague con el toggle (no se migra nada).
export const CARRUSEL_FLAGS = {
    flechas:     { label: 'Flechas', def: true,  title: 'Mostrar u ocultar las flechas' },
    puntos:      { label: 'Puntos',  def: true,  title: 'Mostrar u ocultar los puntos' },
    pausa_hover: { label: 'Pausa',   def: false, title: 'Pausar la rotación al pasar el ratón por encima' },
};

/** Valor EFECTIVO de un interruptor: prop si existe, default de CARRUSEL_FLAGS si no. */
export const leerFlag = (props, nombre) => {
    const meta = CARRUSEL_FLAGS[nombre];
    const def = meta ? meta.def : true;
    const v = props ? props[nombre] : undefined;
    if (v === undefined || v === null || v === '') return def;
    return v !== false && v !== 'false' && v !== 0 && v !== '0';
};

// Mismo placeholder-por-host que gallery.js: el extractor lo detecta por `placehold.co`
// (NO por la subcadena 'placeholder', que no aparece en la URL — gotcha nº21).
const PLACEHOLDER = 'https://placehold.co/1600x900/cccccc/999999?text=Imagen';

// ¿`image` es una KEYWORD de Unsplash por resolver y no una URL/ruta/data: ya lista?
// Espejo de `editor/unsplash.js::isResolved` (un bloque no puede importar de js/editor/:
// rompería la cadena registry→bloque, gotcha nº3). Si cambias una, cambia la otra.
const esKeyword = (v) => !!v && !v.startsWith('http') && !v.startsWith('/') && !v.startsWith('data:');

const _txt = (v) => (typeof v === 'string' ? v : '');

/**
 * Normaliza lo que llega en `props.slides`. El array lo escribe el LLM y lo edita el
 * cliente: un `null`, un número o un string suelto reventaba el `.startsWith` de abajo,
 * y un renderer que lanza deja la PÁGINA ENTERA sin pintar (renderPage monta todo el
 * HTML antes de asignarlo). Mismo saneo que gallery.js::normalizarItem.
 */
const normalizarSlide = (s) => {
    if (typeof s === 'string') return { image: s, alt: '', title: '', cta: '', cta_url: '' };
    if (!s || typeof s !== 'object') return null;
    return {
        ...s,
        image: _txt(s.image),
        alt: _txt(s.alt),
        title: _txt(s.title),
        cta: _txt(s.cta),
        cta_url: _txt(s.cta_url),
    };
};

// Fallback VISUAL cuando el bloque llega sin `slides` (props perdidos / generación
// incompleta): tres diapositivas VACÍAS. A propósito sin copy de ejemplo — el titular de
// arranque vive en la plantilla de inserción (editor/tools.js), que es contenido que el
// cliente edita, no texto fijo del producto horneado en el renderer.
// EXPORTADO porque `core/extractor.js` materializa el array con ESTE tamaño: en el editor
// solo hay una diapositiva VISIBLE, así que sembrar "tantas como haya en el DOM" habría
// dejado el array en 1 y borrado las otras dos al guardar.
export const DEFAULT_SLIDES = [
    { image: '', alt: '', title: '', cta: '', cta_url: '' },
    { image: '', alt: '', title: '', cta: '', cta_url: '' },
    { image: '', alt: '', title: '', cta: '', cta_url: '' },
];

// ── Diapositiva EN EDICIÓN: estado de UI, JAMÁS props ────────────────────────────────
// Mapa en memoria del módulo, por índice de BLOQUE. Si viviera en props se guardaría en
// `datos_json_builder` y viajaría al sitio publicado — el mismo criterio que el destino
// global de botones (invariante nº20). Se pierde al recargar y vuelve a la primera
// diapositiva: es lo correcto, no es un dato del cliente.
const _slideEnEdicion = new Map();

/** Índice de la diapositiva que se edita, CLAMPADO al total real (auto-sana tras borrar). */
export const getSlideEnEdicion = (blockIndex, total) => {
    const max = Math.max(0, (Number(total) || 0) - 1);
    const v = _slideEnEdicion.get(blockIndex);
    const n = (typeof v === 'number' && isFinite(v)) ? v : 0;
    return Math.min(max, Math.max(0, n));
};

/** La fija el dispatch de las pestañas (editor/tools.js). */
export const setSlideEnEdicion = (blockIndex, slideIndex) => {
    const n = Number(slideIndex);
    _slideEnEdicion.set(blockIndex, (typeof n === 'number' && isFinite(n) && n > 0) ? Math.floor(n) : 0);
};

export const carruselBlock = (rawProps, index = 0) => {
    const p = safe(rawProps);
    const variant = p.variant || 'v1';
    const altura = clampAltura(p.altura);
    const intervalo = clampIntervalo(p.intervalo);
    const flechas = leerFlag(p, 'flechas');
    const puntos = leerFlag(p, 'puntos');
    const pausaHover = leerFlag(p, 'pausa_hover');

    const slidesProps = (Array.isArray(p.slides) ? p.slides : [])
        .map(normalizarSlide)
        .filter(Boolean);
    const slides = slidesProps.length > 0 ? slidesProps : DEFAULT_SLIDES;

    // ── QUÉ SE VE EN EL EDITOR ────────────────────────────────────────────────────────
    // Se marca la diapositiva en edición y el CSS oculta las demás
    // (`.editor-active .carrusel-slide:not(.is-editing) { display:none }`).
    // AQUÍ NO SE FILTRA EL ARRAY, y eso NO es pereza: `compileSiteHtml` (viewer.js)
    // re-renderiza cada bloque llamando al registry con `editorMode` TODAVÍA en `true`
    // —se publica DESDE el editor, sin salir a "Ver Sitio"—, así que un render que
    // devolviera una sola diapositiva publicaría una web con una sola diapositiva.
    // Ocultar por CSS mantiene además intacto el contrato del extractor, que lee del DOM.
    const esEditor = !!store.get('editorMode');
    const enEdicion = esEditor ? getSlideEnEdicion(index, slides.length) : -1;

    // v1 = fundido (Splide 'fade': ni clona ni desplaza). El resto = bucle continuo.
    // OJO: 'fade' NO da la vuelta por sí solo — llega a la última diapositiva y se planta,
    // dejando el autoplay muerto. Por eso el <div> emite `data-splide-rewind="true"`, que es
    // lo que la devuelve a la primera. En 'loop' Splide lo ignora (el bucle ya envuelve).
    const tipoSplide = variant === 'v1' ? 'fade' : 'loop';
    const esThumbs = variant === 'v4';
    const idMain = `carrusel-main-${index}`;
    const idThumbs = `carrusel-thumbs-${index}`;

    // Los botones de IMAGEN de la diapositiva NO van en un `.item-tools` sobre la foto: ahí
    // el CSS genérico los coloca en `top:15px; right:15px` de la diapositiva, que con el
    // editor a altura real cae JUSTO DEBAJO del panel de herramientas del bloque — quedaban
    // tapados—, y además solo aparecen al pasar el ratón, que sobre una foto a pantalla
    // completa no se descubre. Viven en la barra de pestañas, junto a "borrar", que es el
    // sitio estable para lo que actúa sobre la diapositiva EN EDICIÓN.
    // El image-picker se resuelve solo con data-array/data-index: catalog.js trata cualquier
    // item de array de forma genérica, así que NO hay que registrar el tipo en ninguna lista.
    // OJO a la asimetría REAL entre botones hermanos: .image-picker-btn usa `data-index` y
    // .image-remove-btn usa `data-item-index`.

    const renderImg = (s, i) => {
        const keyword = esKeyword(s.image);
        const src = keyword ? PLACEHOLDER : (s.image || PLACEHOLDER);
        // LCP: una imagen YA resuelta se hornea con su src real (el navegador la pide al
        // parsear el HTML). La PRIMERA diapositiva es el LCP de una página que abre con
        // carrusel — y la que `views.py::_build_seo_head` publica en el `<link rel=preload>` —
        // así que es la ÚNICA con `fetchpriority="high"` y la única SIN `loading="lazy"`:
        // marcarla como diferida cancelaría el preload, y repartir la prioridad alta entre
        // todas las fotos la diluye hasta no significar nada (mismo criterio que hero.js).
        const esPrimera = i === 0;
        // `fetchpriority` es EXCLUSIVO de la primera (repartirlo entre todas lo diluye hasta no
        // significar nada). `decoding="async"` NO: va en TODAS, y separarlo del anterior es un
        // arreglo de rendimiento, no cosmética. Sin él el navegador puede decodificar la foto en
        // el HILO PRINCIPAL justo cuando toca pintarla; en un bloque a pantalla completa eso son
        // ~50-200ms por imagen, y en las variantes con bucle Splide CLONA diapositivas, así que
        // hay más <img> de las que se ven. Varias decodificaciones seguidas = el tirón de ~1s al
        // llegar al bloque.
        const prio = (esPrimera && !keyword) ? ' fetchpriority="high"' : '';
        const carga = esPrimera ? '' : ' loading="lazy"';
        const decode = ' decoding="async"';
        // Encuadre POR DIAPOSITIVA (utils/encuadre.js, D-1452): la foto va a sangre con `cover`,
        // así que el foco decide qué se ve. El zoom (`scale`) se compone con el Ken Burns de la v1,
        // que anima `transform`. Sin prop, nada.
        const enc = atributosEncuadre(s);
        const clase = (keyword ? 'carrusel-img unsplash-target' : 'carrusel-img') + enc.clase;
        return `<img class="${clase}" src="${src}"${s.image ? ` data-unsplash="${s.image}"` : ''} ${altAttr(s.alt, s.title, s.image)}${carga}${prio}${decode} style="opacity:${keyword ? '0' : '1'};${enc.estilo}">`;
    };

    // El <h1> lo lleva SOLO la primera diapositiva; el resto son <h2>. La decisión final
    // (si esta página ya tiene <h1> en otro bloque) la reajusta `jerarquiaTitulares`
    // (core/render.js), que degrada este <h1> a <h2> conservando el aspecto.
    const renderSlide = (s, i) => {
        const tag = i === 0 ? 'h1' : 'h2';
        // El botón se pinta SIEMPRE en edición (para poder escribirlo) pero solo VIAJA al
        // publicado si tiene texto: sin cta, el ancla va marcada .editor-only y
        // compileSiteHtml la elimina. El href pasa por el embudo único de CTA
        // (utils/links.js): enlace propio → destino global del sitio → '#'.
        const ctaCls = s.cta ? 'btn-action carrusel-cta' : 'btn-action carrusel-cta editor-only is-empty';
        const edit = i === enEdicion ? ' is-editing' : '';
        return `
        <div class="splide__slide carrusel-slide sortable-item${edit}" data-item-index="${i}" data-array-name="slides">
            <div class="carrusel-media">${renderImg(s, i)}</div>
            <div class="carrusel-velo"></div>
            <div class="carrusel-caption">
                <${tag} class="carrusel-title" data-field="title" contenteditable="true">${s.title}</${tag}>
                ${conSecundario(`<a ${ctaAnchorAttrs(s.cta_url)} class="${ctaCls}" data-field="cta" contenteditable="true"${atributoDeBoton(p, 'cta', s)}>${s.cta}</a>`, p, { item: s })}
            </div>
        </div>`;
    };

    // Tira de miniaturas (v4). Es una SEGUNDA instancia de Splide en modo navegación que
    // splide-init.js empareja con la principal vía data-splide-sync (Splide 4.1.4 admite
    // sync() después de mount). Sin JS (editor) queda como una tira estática que resalta
    // la diapositiva en edición: informativa, no interactiva.
    // La <img> lleva los MISMOS atributos de hidratación que la grande (clase
    // `unsplash-target` + `data-unsplash`): sin ellos `editor/unsplash.js` ni la miraba y
    // una miniatura con keyword se quedaba en el placeholder gris PARA SIEMPRE en el sitio
    // publicado. Por eso el atenuado de las no activas es `filter: brightness()` y ya no
    // `opacity` (site.css): unsplash.js escribe `style.opacity = '1'` al resolver y habría
    // borrado el atenuado — dos escritores sobre la misma propiedad.
    const renderThumb = (s, i) => {
        const keyword = esKeyword(s.image);
        // La miniatura enseña el MISMO encuadre que su diapositiva (solo el foco: a ese tamaño el
        // zoom no aporta y su caja no recorta).
        const foco = atributosEncuadre({ image_pos_x: s.image_pos_x, image_pos_y: s.image_pos_y });
        const clase = keyword ? 'carrusel-thumb-img unsplash-target' : 'carrusel-thumb-img';
        const edit = i === enEdicion ? ' is-editing' : '';
        return `
                    <div class="splide__slide carrusel-thumb${edit}">
                        <img class="${clase}" src="${keyword ? PLACEHOLDER : (s.image || PLACEHOLDER)}"${s.image ? ` data-unsplash="${s.image}"` : ''} ${altAttr(s.alt, s.title, s.image)} loading="lazy"${(keyword || foco.estilo) ? ` style="${keyword ? 'opacity:0;' : ''}${foco.estilo.trim()}"` : ''}>
                    </div>`;
    };

    const thumbs = esThumbs ? `
        <div class="splide carrusel-thumbs" id="${idThumbs}"
             data-splide-per-page="5" data-splide-type="slide" data-splide-gap="0.6rem"
             data-splide-autoplay="false" data-splide-arrows="false"
             data-splide-pagination="false" data-splide-navigation="true">
            <div class="splide__track">
                <div class="splide__list">
                    ${slides.map(renderThumb).join('')}
                </div>
            </div>
        </div>` : '';

    // ── BARRA DEL EDITOR (.editor-only → compileSiteHtml la elimina) ──────────────────
    // Dos cosas en la MISMA banda superior, en FLUJO (ya no flotando sobre la foto, que
    // era donde tapaba el titular que el cliente está escribiendo):
    //   · PESTAÑAS  → qué diapositiva se edita, añadir y borrar.
    //   · AJUSTES   → altura, ritmo y qué controles se ven en el sitio.
    // Mismo patrón que la cabecera de tamaño de Marcas/Catálogo (blocks/logos.js): el
    // render pinta el chrome y el dispatch vive en editor/tools.js (delegación por CLASE,
    // nunca `on*` inline — gotcha nº13). "Borrar" reusa la clase `.item-delete-btn`, así
    // que hereda tal cual la confirmación inline de tools.js (no hay segundo camino
    // destructivo que mantener).
    //
    // NO hay flechas de ORDEN, y es una decisión: convivían con las pestañas en la misma
    // banda y se leían como "ir a la siguiente diapositiva" cuando lo que hacían era
    // REORDENARLA. Al pulsarlas el número activo saltaba pero la foto y el titular se
    // quedaban igual (correcto para un reordenado: sigues viendo la MISMA diapositiva, ya
    // movida de sitio), y eso se percibe como que el botón está roto. Navegar es pinchar el
    // número, que no admite otra lectura. Si algún día hace falta reordenar, que sea un
    // gesto que se vea como tal (arrastrar la pestaña), no dos flechas junto a un índice.
    const pestanas = `
            <div class="carrusel-seg carrusel-tabs" role="group" aria-label="Diapositivas">
                ${slides.map((_, i) => `<button class="carrusel-tab${i === enEdicion ? ' is-active' : ''}" data-slide="${i}" title="Editar la diapositiva ${i + 1}">${i + 1}</button>`).join('')}
                <button class="carrusel-tab-add" title="Añadir una diapositiva">+</button>
                <button class="image-picker-btn carrusel-slide-img" data-array="slides" data-index="${enEdicion}" title="Cambiar la imagen de esta diapositiva">${window.icons.image}</button>
                ${(slides[enEdicion] && slides[enEdicion].image) ? `<button class="image-remove-btn carrusel-slide-img" data-array="slides" data-item-index="${enEdicion}" title="Quitar la imagen de esta diapositiva">${window.icons.imageOff}</button>` : ''}
                <button class="item-delete-btn carrusel-slide-del" data-array="slides" data-index="${enEdicion}" title="Borrar esta diapositiva">${window.icons.trash}</button>
            </div>`;

    const ajustes = `
        <div class="carrusel-editor-bar editor-only">
            ${pestanas}
            <div class="carrusel-seg" role="group" aria-label="Altura de la banda">
                ${CARRUSEL_ALTURAS.map(a => `<button class="carrusel-altura-btn${altura === a.id ? ' is-active' : ''}" data-altura="${a.id}" title="Altura ${a.label.toLowerCase()}">${a.label}</button>`).join('')}
            </div>
            <div class="carrusel-seg carrusel-ritmo" role="group" aria-label="Segundos entre diapositivas">
                <button class="carrusel-int-dec" title="Menos segundos (0 = sin rotación automática)">&minus;</button>
                <span class="carrusel-int-val">${intervalo === 0 ? 'Sin auto' : intervalo + 's'}</span>
                <button class="carrusel-int-inc" title="Más segundos entre diapositivas">+</button>
            </div>
            <div class="carrusel-seg" role="group" aria-label="Controles visibles">
                ${Object.keys(CARRUSEL_FLAGS).map(k => `<button class="carrusel-flag-btn${leerFlag(p, k) ? ' is-active' : ''}" data-flag="${k}" title="${CARRUSEL_FLAGS[k].title}">${CARRUSEL_FLAGS[k].label}</button>`).join('')}
            </div>
        </div>`;

    // NOTA: el <section> NO lleva `padding:Npx` inline a propósito (igual que el hero).
    // render.js reescribe ese padding a `var(--section-pad-y, Npx)` y aquí eso metería
    // bandas de color por encima y por debajo de una banda que es full-bleed por diseño.
    return `
    <section id="bloque-carrusel-${index}" class="reveal carrusel-section variant-${variant}" data-altura="${altura}"
             style="--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --accent-contrast:${p.accentContrast}; --font:'${p.fontFamily || "Inter"}';">
        ${ajustes}
        <div class="splide carrusel-splide" id="${idMain}"
             data-splide-per-page="1" data-splide-type="${tipoSplide}" data-splide-gap="0"
             data-splide-autoplay="${intervalo > 0}" data-splide-interval="${intervalo * 1000}"
             data-splide-arrows="${flechas}" data-splide-pagination="${puntos}"
             data-splide-rewind="true"
             data-splide-pause-on-hover="${pausaHover}"${esThumbs ? ` data-splide-sync="${idThumbs}"` : ''}>
            <div class="splide__track">
                <div class="splide__list">
                    ${slides.map(renderSlide).join('')}
                </div>
            </div>
        </div>
        ${thumbs}
    </section>`;
};
